import { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileArrowUp, FileText, FilePdf, Image as ImageIcon, X } from '@phosphor-icons/react';

// FileDropzone is the controlled multi-file picker used by the feedback
// page (and any future surface that wants the same drag/drop + thumb-
// preview UX). Strictly controlled — the parent owns the file list and
// renders it through `value`. The component reports change via `onChange`
// and validation errors via the local error pill.
//
// Why a custom component (not react-dropzone): the existing app keeps the
// dep surface tight; the drag/drop semantics needed here are small
// enough that a hand-rolled component is shorter than the dep entry.
//
// Validation strategy: client-side checks for size + accept-list are
// best-effort. The server re-runs each check (with magic-byte sniffing
// rather than declared MIME type), so a hostile upload that bypasses
// the client always fails at the server boundary.

export interface FileDropzoneProps {
  value: File[];
  onChange: (files: File[]) => void;
  /** Accepted MIME types (sniffed by the browser file dialog). */
  accept: string;
  /** Closed set of acceptable MIME types (rechecked after selection). */
  acceptedMimeTypes: ReadonlyArray<string>;
  maxFiles: number;
  /** Per-file byte limit. */
  maxBytes: number;
  /** Translated label for the empty-state CTA. */
  cta?: string;
  /** Disabled = no dragdrop, no click, no remove. */
  disabled?: boolean;
}

interface RejectedFile {
  name: string;
  reason: 'too_large' | 'unsupported_type' | 'too_many';
}

export function FileDropzone({
  value,
  onChange,
  accept,
  acceptedMimeTypes,
  maxFiles,
  maxBytes,
  cta,
  disabled = false,
}: FileDropzoneProps) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);

  const acceptFiles = useCallback(
    (incoming: FileList | File[]) => {
      const arr = Array.from(incoming);
      const fresh: File[] = [];
      const rej: RejectedFile[] = [];

      for (const f of arr) {
        if (value.length + fresh.length >= maxFiles) {
          rej.push({ name: f.name, reason: 'too_many' });
          continue;
        }
        if (f.size > maxBytes) {
          rej.push({ name: f.name, reason: 'too_large' });
          continue;
        }
        if (!acceptedMimeTypes.includes(f.type)) {
          // Some browsers report empty .type for known extensions; fall
          // back to checking the suffix for the office xlsx case.
          const ok = f.type === '' && f.name.toLowerCase().endsWith('.xlsx');
          if (!ok) {
            rej.push({ name: f.name, reason: 'unsupported_type' });
            continue;
          }
        }
        fresh.push(f);
      }

      setRejected(rej);
      if (fresh.length > 0) {
        onChange([...value, ...fresh]);
      }
    },
    [acceptedMimeTypes, maxBytes, maxFiles, onChange, value],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        acceptFiles(e.dataTransfer.files);
      }
    },
    [acceptFiles, disabled],
  );

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (disabled) return;
    setDragging(true);
  }
  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
  }

  function removeAt(index: number) {
    const next = value.slice();
    next.splice(index, 1);
    onChange(next);
  }

  const atLimit = value.length >= maxFiles;

  return (
    <div className="space-y-2">
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed px-4 py-6 text-center text-sm transition ${
          disabled
            ? 'cursor-not-allowed border-border/40 bg-muted/30 text-foreground/40'
            : dragging
              ? 'border-lime bg-lime/10 text-foreground'
              : 'border-border/60 bg-background text-foreground/70 hover:border-border'
        }`}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={accept}
          disabled={disabled || atLimit}
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) acceptFiles(e.target.files);
            // Reset so re-selecting the same file fires onChange again.
            e.target.value = '';
          }}
        />
        <FileArrowUp size={24} weight="bold" className="mx-auto mb-1 text-foreground/60" />
        <label
          htmlFor={inputId}
          className={`block cursor-pointer font-semibold ${
            disabled || atLimit ? 'cursor-not-allowed text-foreground/40' : 'text-foreground'
          }`}
        >
          {cta ?? t('fileDropzone.cta')}
        </label>
        <p className="mt-1 text-xs text-foreground/60">
          {t('fileDropzone.hint', {
            max: maxFiles,
            mb: Math.round(maxBytes / 1024 / 1024),
          })}
        </p>
      </div>

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-2 rounded-2xl border border-border/60 bg-card px-3 py-2 text-sm"
            >
              <FileIcon type={f.type} />
              <span className="min-w-0 flex-1 truncate" title={f.name}>
                {f.name}
              </span>
              <span className="shrink-0 text-xs text-foreground/60">
                {formatBytes(f.size)}
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                disabled={disabled}
                aria-label={t('fileDropzone.remove')}
                className="rounded-pill p-1 text-foreground/60 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              >
                <X size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {rejected.length > 0 && (
        <ul className="space-y-1" role="alert">
          {rejected.map((r, idx) => (
            <li
              key={`${r.name}-${idx}`}
              className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {t(`fileDropzone.error.${r.reason}`, { name: r.name })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileIcon({ type }: { type: string }) {
  if (type.startsWith('image/')) {
    return <ImageIcon size={18} weight="bold" className="text-foreground/60" />;
  }
  if (type === 'application/pdf') {
    return <FilePdf size={18} weight="bold" className="text-foreground/60" />;
  }
  return <FileText size={18} weight="bold" className="text-foreground/60" />;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
